import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, CheckCircle, Heart, Users, Lightbulb, Handshake, ArrowRight } from 'lucide-react';
import { clinician, missionValues } from '../data/content';
import BookingModal from '../components/common/BookingModal';

const AboutPage = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const valueIcons = [Heart, Lightbulb, Users, Handshake];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge badge-teal mb-4">About Us</span>
            <h1 className="heading-hero mb-6">Dedicated to Your Swallowing Health</h1>
            <p className="body-large">
              Our mission is to provide exceptional, evidence-based dysphagia care 
              that empowers you to eat safely and enjoy the social experience of meals.
            </p>
          </div>
        </div>
      </section>

      {/* Clinician Profile */}
      <section className="section bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Clinician Photo - Editorial Portrait */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                {clinician.photo ? (
                  <img 
                    src={clinician.photo}
                    alt={`${clinician.name}, ${clinician.credentials}, Speech-Language Pathologist`}
                    className="w-full aspect-[4/5] object-cover object-top rounded-2xl shadow-2xl"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                {/* Fallback if image doesn't load */}
                <div 
                  className={`aspect-[4/5] bg-gradient-to-br from-teal-100 to-teal-50 rounded-2xl flex-col items-center justify-center ${clinician.photo ? 'hidden' : 'flex'}`}
                >
                  <div className="text-center p-8">
                    <div className="w-32 h-32 bg-teal-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <Users size={48} className="text-teal-600" />
                    </div>
                    <p className="text-sm text-teal-600 font-medium">GILDA PHOTO ASSET REQUIRED</p>
                  </div>
                </div>
                {/* Floating credential badge */}
                <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                      <Award size={24} className="text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{clinician.experience}</p>
                      <p className="text-sm text-gray-500">Clinical Experience</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Content */}
            <div className="order-1 lg:order-2">
              <span className="badge badge-teal mb-4">Meet Your Clinician</span>
              <h2 className="heading-1 mb-2">{clinician.name}</h2>
              <p className="text-teal-600 font-medium mb-2">{clinician.credentials}</p>
              <p className="body-small text-gray-500 mb-6">{clinician.subtitle || clinician.title}</p>

              <div className="body-large mb-8 whitespace-pre-line">{clinician.bio}</div>

              {/* Specialties */}
              <div className="mb-8">
                <h3 className="heading-3 mb-4">Areas of Expertise</h3>
                <div className="space-y-3">
                  {clinician.specialties.map((specialty, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle size={18} className="text-teal-600 flex-shrink-0" />
                      <span className="body-medium">{specialty}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div className="mb-8">
                <h3 className="heading-3 mb-4">Certifications & Affiliations</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {clinician.certifications.map((cert, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
                      <Award size={16} className="text-teal-600 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setIsBookingOpen(true)}
                className="btn-primary"
              >
                Schedule with {clinician.name.split(' ')[0]}
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="badge badge-teal mb-4">Our Purpose</span>
            <h2 className="heading-1 mb-6">Mission & Values</h2>
            <p className="body-large">{missionValues.mission}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {missionValues.values.map((value, index) => {
              const IconComponent = valueIcons[index] || Heart;
              return (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <IconComponent size={24} className="text-teal-600" />
                  </div>
                  <h3 className="heading-3 mb-2">{value.title}</h3>
                  <p className="body-small text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-1 mb-4">Let's Work Together</h2>
            <p className="body-large mb-8">
              Ready to take the first step toward improved swallowing? 
              We're here to help you on your journey.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setIsBookingOpen(true)}
                className="btn-primary"
              >
                Book a Consultation
                <ArrowRight size={16} />
              </button>
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} sourcePage="about" />
    </>
  );
};

export default AboutPage;
